import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, FileText, Copy } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { AdminHeader } from "@/components/AdminHeader";
import { Button } from "@/components/ui/button";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return String(iso); }
};

const STATUS_STYLES = {
  sent: "text-blue-700 border-blue-200 bg-blue-50",
  accepted: "text-emerald-700 border-emerald-200 bg-emerald-50",
  converted: "text-emerald-700 border-emerald-200 bg-emerald-50",
  expired: "text-stone-500 border-stone-200 bg-stone-100",
};

export default function QuotesTab() {
  const [quotes, setQuotes] = useState([]);
  const load = () => api.get("/admin/quotes").then((r) => setQuotes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const statusOf = (q) => {
    if (q.booking_id) return q.status === "accepted" ? "accepted" : "converted";
    if (q.expires_at && new Date(q.expires_at) < new Date()) return "expired";
    return "sent";
  };

  const convert = async (q) => {
    try {
      const { data } = await api.post(`/admin/quotes/${q.id}/convert`);
      toast.success(`Quote ${q.quote_ref} converted to booking ${data.booking.booking_ref}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const resend = async (q) => {
    try {
      await api.post(`/admin/quotes/${q.id}/resend`);
      toast.success(`Quote ${q.quote_ref} re-sent to ${q.customer_name}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="quotes-tab">
      <AdminHeader
        testid="admin-header-quotes"
        eyebrow="Sales"
        title="Quotes"
        description="Price quotes sent to guests — the accept link auto-creates a pending booking and takes the guest to payment."
        actions={<Button data-testid="quotes-refresh" variant="outline" onClick={load} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>}
      />
      {quotes.length > 0 && (() => {
        const total = quotes.length;
        const closed = quotes.filter((q) => q.booking_id).length;
        const revenue = quotes.filter((q) => q.booking_id).reduce((s, q) => s + Number(q.total || 0), 0);
        const rate = total ? Math.round((closed / total) * 100) : 0;
        return (
          <div data-testid="quotes-conversion-card" className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 flex flex-wrap items-center gap-x-8 gap-y-2 font-ui">
            <p className="text-[10px] tracking-[0.25em] uppercase text-emerald-800 w-full sm:w-auto">Quote → Booking conversion</p>
            <p className="text-sm"><span data-testid="quotes-conversion-count" className="text-lg font-semibold text-emerald-700">{closed}</span> <span className="text-muted-foreground text-xs">of</span> <span className="text-lg font-semibold text-[#1A1A1A]">{total}</span> <span className="text-muted-foreground text-xs">accepted/converted</span></p>
            <p className="text-sm"><span data-testid="quotes-conversion-rate" className="text-lg font-semibold text-[#1A1A1A]">{rate}%</span> <span className="text-muted-foreground text-xs">close rate</span></p>
            <p className="text-sm"><span data-testid="quotes-conversion-revenue" className="text-lg font-semibold text-[#1A1A1A]">${revenue.toFixed(2)}</span> <span className="text-muted-foreground text-xs">booked from quotes</span></p>
          </div>
        );
      })()}
      {quotes.length === 0 ? (
        <div className="rounded-2xl border border-[#E5DED5] bg-white p-10 text-center">
          <FileText className="mx-auto h-8 w-8 text-[#9B8F80]" />
          <p className="mt-3 text-sm font-ui text-muted-foreground">No quotes yet. Create one from Bookings → New booking → “Send as quote only”.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {quotes.map((q) => {
            const st = statusOf(q);
            return (
              <div key={q.id} data-testid={`quote-row-${q.id}`} className="rounded-xl border border-[#E5DED5] bg-white px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="font-ui font-semibold text-[#1A1A1A]">{q.quote_ref}</span>
                <span className={`border rounded-full px-2 py-0.5 text-[10px] font-ui uppercase tracking-wider ${STATUS_STYLES[st]}`} data-testid={`quote-status-${q.id}`}>{st}</span>
                <span className="text-sm font-ui text-[#4A4A4A]">{q.customer_name}{q.customer_email ? ` · ${q.customer_email}` : ""}</span>
                <span className="text-xs font-ui text-muted-foreground">{q.vehicle_name} · {q.start_date} → {q.end_date}</span>
                <span className="text-sm font-ui font-semibold text-[#1A1A1A]">${Number(q.total).toFixed(2)}</span>
                <span className="text-xs font-ui text-muted-foreground">sent {fmtDate(q.created_at)}</span>
                <span className="flex-1" />
                {st === "sent" && (
                  <>
                    <Button data-testid={`quote-convert-${q.id}`} size="sm" onClick={() => convert(q)} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui">Convert to booking</Button>
                    <Button data-testid={`quote-resend-${q.id}`} size="sm" variant="outline" onClick={() => resend(q)} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui">
                      <RefreshCw className="h-3.5 w-3.5 mr-1" /> Resend
                    </Button>
                  </>
                )}
                <Button data-testid={`quote-duplicate-${q.id}`} size="sm" variant="outline" onClick={async () => { try { const { data } = await api.post(`/admin/quotes/${q.id}/duplicate`); toast.success(`Duplicated as ${data.quote.quote_ref} — hit Resend to send it`); load(); } catch (e) { toast.error(errMsg(e)); } }} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui">
                  <Copy className="h-3.5 w-3.5 mr-1" /> Duplicate
                </Button>
                {st === "accepted" && <span className="text-xs font-ui text-emerald-700">guest accepted → booking created</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
